import { useLogger } from '#layers/base/composables/useLogger'

interface Page<T> {
  items: T[]
}

export interface CategoryGroupType {
  handle: string
  i18nKey: string
  description: Record<string, string>
}

type SubCategoryGroup = CategoryGroupType

export const useCategoryGroup = defineStore('category-group', () => {
  const { baseURL } = useRuntimeConfig().app
  const log = useLogger('useCategoryGroup')

  const {
    data: categoryGroupsData,
    pending: isCategoryGroupsPending,
    refresh: refreshCategoryGroups,
    error: categoryGroupsError,
  } = useFetch<Page<CategoryGroupType>>('/api/easyApi/categories', {
    baseURL,
    server: false,
    immediate: true,
    onResponseError({ error }) {
      log.warn('Failed to fetch category groups', { error })
    },
  })

  const subCategoriesHandle = ref<string>('')

  const categoriesCache = ref<Map<string, CategoryGroupType[]>>(new Map())
  const pendingFetches = new Map<string, Promise<CategoryGroupType[]>>()
  const pendingHandles = ref<Set<string>>(new Set())
  const currentCategoriesHandle = ref<string>('')
  const categoriesFetchError = ref<Error | null>(null)

  const {
    data: subCategoriesData,
    pending: isSubCategoriesPending,
    refresh: refreshSubCategories,
    error: subCategoriesError,
  } = useFetch<Page<SubCategoryGroup>>(
    () => `/api/easyApi/categories?cHandle=${subCategoriesHandle.value}`,
    {
      baseURL,
      key: () => `subcategories-${subCategoriesHandle.value}`,
      watch: [subCategoriesHandle],
      server: false,
      lazy: true,
      immediate: false,
      onResponseError({ error }) {
        log.warn('Failed to fetch subcategories for handle', { handle: subCategoriesHandle.value, error })
      },
    })

  const categoryGroup = computed(() => categoryGroupsData.value?.items ?? [])
  const subCategories = computed(() => subCategoriesData.value?.items ?? [])

  const categories = computed(() => {
    if (!currentCategoriesHandle.value) return []
    return categoriesCache.value.get(currentCategoriesHandle.value) ?? []
  })

  const isCategoriesPending = computed(() => {
    return pendingHandles.value.has(currentCategoriesHandle.value)
  })

  const allCategoriesByGroup = computed(() => Object.fromEntries(categoriesCache.value))

  async function fetchCategoriesForGroup(handle: string): Promise<CategoryGroupType[]> {
    if (categoriesCache.value.has(handle)) {
      return categoriesCache.value.get(handle)!
    }

    if (pendingFetches.has(handle)) {
      return pendingFetches.get(handle)!
    }

    pendingHandles.value = new Set([...pendingHandles.value, handle])

    const fetchPromise = $fetch<Page<CategoryGroupType>>(
      `/api/easyApi/categories?gHandle=${handle}`,
      { baseURL },
    ).then((data) => {
      const items = data?.items ?? []
      categoriesCache.value.set(handle, items)
      pendingFetches.delete(handle)
      pendingHandles.value = new Set([...pendingHandles.value].filter(h => h !== handle))
      return items
    }).catch((error) => {
      log.warn('Failed to fetch categories', { handle, error })
      categoriesFetchError.value = error as Error
      pendingFetches.delete(handle)
      pendingHandles.value = new Set([...pendingHandles.value].filter(h => h !== handle))
      return []
    })

    pendingFetches.set(handle, fetchPromise)
    return fetchPromise
  }

  async function fetchAllCategories(): Promise<void> {
    const groups = categoryGroupsData.value?.items
    if (!groups?.length) return

    await Promise.all(
      groups.map(group => fetchCategoriesForGroup(group.handle)),
    )
  }

  function getCategoryGroups() {
    refreshCategoryGroups()
  }

  async function getCategories(handle: string): Promise<CategoryGroupType[]> {
    currentCategoriesHandle.value = handle
    return fetchCategoriesForGroup(handle)
  }

  function getSubCategories(handle: string) {
    subCategoriesHandle.value = handle
  }

  function setCurrentCategoriesHandle(handle: string) {
    currentCategoriesHandle.value = handle
  }

  function $reset() {
    currentCategoriesHandle.value = ''
    subCategoriesHandle.value = ''
    categoriesCache.value.clear()
    pendingFetches.clear()
    pendingHandles.value = new Set()
    categoriesFetchError.value = null
    refreshCategoryGroups()
  }

  return {
    categoryGroup,
    categories,
    subCategories,
    allCategoriesByGroup,

    isCategoryGroupsPending,
    isCategoriesPending,
    isSubCategoriesPending,

    categoryGroupsError,
    categoriesFetchError,
    subCategoriesError,

    getCategoryGroups,
    getCategories,
    getSubCategories,
    fetchAllCategories,
    fetchCategoriesForGroup,
    setCurrentCategoriesHandle,
    refreshCategoryGroups,
    refreshSubCategories,
    $reset,
  }
})
